// apps/shell/src/state/BusProvider.tsx — Tier 1 V3 Slice 1 task 1.14.
//
// Wires the cross-tab bus + IDB-backed KVStore + tab presence registry into
// the React tree. Publishes them via @perlab/ui's BusContext / KvContext so
// consumer hooks (useTheme — Slice 1; useSidebar — Slice 2) can subscribe
// without an apps/shell ←→ packages/ui circular import.
//
// Per spec §3.2.3, the bus is FAIL-CLOSED:
//   - missing tenant/user → NoopBus (createBus does this)
//   - explicit `?bus=off` URL flag → NoopBus directly (this file does that)
//
// `?bus=off` rationale (per TL ruling, drain 2b):
//   We construct `new NoopBus({ real tenant/user })` directly instead of
//   passing empty strings to createBus. createBus's empty-string path logs
//   a false "auth not resolved" error and tags the NoopBus with
//   `_unresolved_`, which would poison any tenant-checked subscribers that
//   arm before bus-off detection. Going direct preserves identity.
//
// `?bus=debug` rationale: Slice 1 has no devtools surface yet (Slice 2 ships
// BusDevtools). For now we tolerate the flag silently — it's not an error,
// just a no-op until Slice 2 lands the overlay.
//
// Mount placement (App.tsx): below ContextChainProvider + AskAIProvider,
// above SidebarProvider per spec §7. Reading `currentContextKey` from
// useContextChain() requires ContextChain to be the ancestor.

import { useEffect, useMemo, useRef, type ReactNode } from "react";
import { useAuth } from "@perlab/auth-bridge";
import { BusContext, KvContext } from "@perlab/ui";
import {
  createBus,
  createTabRegistry,
  NoopBus,
  type CrossTabBus,
  type KVStore,
  type TabRegistry,
} from "@perlab/cross-tab";
import { IdbKvStore } from "@perlab/cross-tab-idb";
import { useContextChain } from "./ContextChain";

/**
 * Parse the bus URL flag once at module evaluation time. Three values:
 *   - 'off'   → force NoopBus regardless of auth state
 *   - 'debug' → reserved for Slice 2 BusDevtools overlay; same as default for now
 *   - null/anything else → factory-managed via createBus()
 *
 * Reading window.location.search is SSR-safe via the typeof check; in non-
 * browser realms the flag is always null and createBus path is taken.
 */
function readBusFlag(): "off" | "debug" | null {
  if (typeof window === "undefined") return null;
  const v = new URLSearchParams(window.location.search).get("bus");
  if (v === "off" || v === "debug") return v;
  return null;
}

export interface BusProviderProps {
  children: ReactNode;
}

export function BusProvider({ children }: BusProviderProps) {
  // 6.5 de-mock: identity now comes from the REAL session (via AppShell's /auth/session), not the
  // old hardcoded DEFAULT_USER. `user` may be null (signed-out) — key on empty strings then, which
  // createBus already treats as fail-closed (NoopBus). AppShell gates render until identity hydrates,
  // so in practice `user` is the resolved session actor here.
  const { user } = useAuth();
  const tenantId = user?.tenant ?? "";
  const userId = user?.userId ?? "";

  // Read the URL flag once — module evaluation is at first render of this
  // component, which happens after the BrowserRouter has hydrated location.
  // Stable across re-renders (the flag does not change without a navigation).
  const busFlag = useMemo(() => readBusFlag(), []);

  // KV store — SAME identity for the lifetime of the provider mount. IDB
  // handles are lazy (first put/get opens the DB) so constructing this in a
  // useMemo with empty deps is cheap and side-effect-free.
  //
  // Constructed even in `?bus=off` mode: bus-off disables cross-tab messaging
  // but does NOT disable local persistence (V11 parity test requires this).
  const kv = useMemo<KVStore>(() => new IdbKvStore(), []);

  // Bus — memoized on (busFlag, tenantId, userId). Identity changes only when
  // auth flips. Closes the prior bus before creating a new one to release
  // BroadcastChannel resources.
  const bus = useMemo<CrossTabBus>(() => {
    if (busFlag === "off") {
      // Direct NoopBus construction with real tenant/user — preserves the
      // identity for any subscribers that may arm during the off window
      // (e.g. tab.hello traffic — though Noop drops the publish, the tabId
      // is real). See TL ruling drain 2b.
      return new NoopBus({ tenantId, userId });
    }
    // Factory path: createBus handles missing-auth fail-closed itself and
    // returns a NoopBus tagged `_unresolved_` when identity is missing.
    // Slice 1's auth stub always provides non-empty values, so the live
    // BroadcastChannelBus is the expected branch.
    return createBus({ tenantId, userId });
  }, [busFlag, tenantId, userId]);

  // Bus teardown effect — fires when the bus identity changes OR on unmount.
  // useMemo cannot model side-effects, so this useEffect mirrors the bus
  // lifecycle: close the previous bus before letting React garbage-collect
  // the reference. The effect's cleanup runs BEFORE the next effect with a
  // new bus, which matches React's "cleanup-then-setup" sequencing.
  useEffect(() => {
    return () => {
      bus.close();
    };
  }, [bus]);

  // Tab registry — needs the CURRENT context key on every heartbeat. Reading
  // currentContextKey from useContextChain() directly would force a registry
  // re-create on every route change (5s heartbeat cadence would be defeated).
  // Pattern: a ref mirrors the latest key; getContextKey closures read the
  // ref. Registry then memoizes on [bus] alone (per TL approval drain 1).
  const { currentContextKey } = useContextChain();
  const currentContextKeyRef = useRef(currentContextKey);
  useEffect(() => {
    currentContextKeyRef.current = currentContextKey;
  }, [currentContextKey]);

  const tabRegistry = useMemo<TabRegistry>(() => {
    return createTabRegistry({
      bus,
      getContextKey: () => currentContextKeyRef.current,
    });
  }, [bus]);

  // Registry teardown — same pattern as bus.close(). Heartbeat timers and
  // visibilitychange / pagehide listeners are released here.
  useEffect(() => {
    return () => {
      tabRegistry.stop();
    };
  }, [tabRegistry]);

  // Provider order: Bus outer, Kv inner. No real ordering constraint between
  // them since they expose independent contexts, but nesting Kv inside Bus
  // is the canonical "transport over storage" mental model.
  return (
    <BusContext.Provider value={bus}>
      <KvContext.Provider value={kv}>
        {children}
      </KvContext.Provider>
    </BusContext.Provider>
  );
}
